import { Meta, SetterConfig } from '../types';

// 依赖节点类型定义
export interface DependencyNode {
  field: string;          // 字段名
  priority: number;       // 优先级
  dependencies: string[]; // 依赖的字段
  dependents: string[];   // 被谁依赖
  handler: string;        // 处理函数
  updateStrategy?: {      
    when: string[];      // 触发条件
    keepFields: string[]; // 保持不变的字段
  }
}

export class DependencyGraph {
  private nodes: Map<string, DependencyNode>;
  
  constructor(meta: Meta) {
    this.nodes = new Map();
    
    // 从 meta 配置构建依赖图
    meta.setters.props
      .filter(setter => setter.effects)
      .forEach(setter => {
        const { name, effects, priority } = setter;
        this.nodes.set(name, {
          field: name,
          priority: priority || 0,
          dependencies: effects?.dependencies || [],
          dependents: [],
          handler: effects?.handler || '',
          updateStrategy: effects?.updateStrategy
        });
      });
    
    // 建立双向依赖关系
    this.nodes.forEach(node => {
      node.dependencies.forEach(dep => {
        const depNode = this.nodes.get(dep);
        if(depNode) {
          depNode.dependents.push(node.field);
        }
      });
    });
  }

// 使用 Tarjan 算法检测强连通分量(循环依赖)
  detectCycles(): Set<string>[] {
    const visited = new Set<string>();
    const stack = new Set<string>();
    const cycles: Set<string>[] = [];
    
    const dfs = (node: string) => {
      visited.add(node);
      stack.add(node);
      
      const deps = this.nodes.get(node)?.dependencies || [];
      deps.forEach(dep => {
        if(!visited.has(dep)) {
          dfs(dep);
        } else if(stack.has(dep)) {
          // 找到循环依赖
          const cycle = new Set<string>();
          for(const n of stack) {
            cycle.add(n);
            if(n === dep) break;
          }
          cycles.push(cycle);
        }
      });
      
      stack.delete(node);
    };

    this.nodes.forEach((_, node) => {
      if(!visited.has(node)) {
        dfs(node);
      }
    });
    
    return cycles;
  }

  // 获取节点信息
  getNode(field: string): DependencyNode | undefined {
    return this.nodes.get(field);
  }

  // 获取所有节点
  getAllNodes(): Map<string, DependencyNode> {
    return this.nodes;
  }

  // 拓扑排序
  toposort(fields: Set<string>): string[] {
    const result: string[] = [];
    const visited = new Set<string>();
    
    const visit = (node: string) => {
      if(visited.has(node)) return;
      visited.add(node);
      
      const deps = this.nodes.get(node)?.dependencies || [];
      deps.forEach(dep => {
        if(fields.has(dep)) {
          visit(dep);
        }
      });
      
      result.push(node);
    };

    fields.forEach(field => {
      if(!visited.has(field)) {
        visit(field);
      }
    });

    return result;
  }
}